import React, {useEffect, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {useTranslation} from 'react-i18next';
import {FaApple, FaChevronDown, FaChevronUp, FaGooglePlay} from 'react-icons/fa';

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const fadeInImage = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const modalFadeIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.8);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    background-color: ${props => props.theme.colors.backgroundItemCard};
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    text-align: center;
    opacity: 0;
    animation: ${fadeIn} 0.5s ease-out forwards;
    animation-delay: ${props => props.delay}s;
    width: 280px;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4);
    }

    @media (max-width: 768px) {
        margin: 5px;
    }
`;

const BikeInnerContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const InfoContainer = styled.div`
    background-color: ${props => props.theme.colors.background}cc;
    border-radius: 6px;
    padding: 10px;
    margin: 10px 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    animation: ${fadeIn} 0.5s ease-out forwards;
    animation-delay: ${props => props.delay + 0.4}s;
`;

const PriceSectionHeader = styled.button`
    background-color: ${props => props.theme.colors.background}cc;
    border: none;
    border-radius: 6px;
    padding: 10px;
    margin: 10px 0;
    width: 100%;
    text-align: left;
    font-size: 1rem;
    font-weight: 500;
    color: ${props => props.theme.colors.primary};
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: ${props => props.theme.colors.background}ee;
    }

    @media (min-width: 769px) {
        display: none;
    }

    @media (max-width: 768px) {
        font-size: 0.9rem;
    }
`;

const InfoContainerPrices = styled.div`
    background-color: ${props => props.theme.colors.background}cc;
    border-radius: 6px;
    padding: ${props => (props.isOpen || props.isDesktop ? '10px' : '0')};
    margin: ${props => (props.isOpen || props.isDesktop ? '0 0 10px 0' : '0')};
    box-shadow: ${props => (props.isOpen || props.isDesktop ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none')};
    max-height: ${props => (props.isOpen || props.isDesktop ? '500px' : '0')};
    overflow: hidden;
    transition: max-height 0.3s ease-out, opacity 0.3s ease-out, padding 0.3s ease-out;
    opacity: ${props => (props.isOpen || props.isDesktop ? 1 : 0)};
`;

const MultiDayPricesContainer = styled.div`
    background-color: ${props => props.theme.colors.background}cc;
    border-radius: 6px;
    padding: ${props => (props.isOpen || props.isDesktop ? '10px' : '0')};
    margin: ${props => (props.isOpen || props.isDesktop ? '0 0 10px 0' : '0')};
    box-shadow: ${props => (props.isOpen || props.isDesktop ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none')};
    max-height: ${props => (props.isOpen || props.isDesktop ? '500px' : '0')};
    overflow: hidden;
    transition: max-height 0.3s ease-out, opacity 0.3s ease-out, padding 0.3s ease-out;
    opacity: ${props => (props.isOpen || props.isDesktop ? 1 : 0)};
`;

const BikeName = styled.h3`
    color: ${props => props.theme.colors.primary};
    font-size: 1.2rem;
    font-weight: 700;
    margin: 0 0 10px;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const BikeImage = styled.img`
    max-width: 200px;
    border-radius: 5px;
    opacity: 0;
    animation: ${fadeInImage} 0.7s ease-out forwards;
    animation-delay: ${props => props.delay + 0.3}s;
`;

const BikeInfo = styled.p`
    color: ${props => props.theme.colors.text};
    font-size: 0.9rem;
    font-weight: 400;
    margin: 5px 0;

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

const BikePrice = styled.p`
    color: ${props => props.theme.colors.text};
    font-size: 14px;
    font-weight: 400;
    margin: 5px 0;
    width: 100%;
    text-align: left;
    animation: ${fadeIn} 0.5s ease-out forwards;

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

const Placeholder = styled.div`
    height: 200px;
    width: 200px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
    margin: 0 auto;

    @keyframes shimmer {
        0% {
            background-position: 200% 0;
        }
        100% {
            background-position: -200% 0;
        }
    }
`;

const BookButton = styled.button`
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    margin-top: 10px;
    transition: all 0.3s ease;
    animation: ${fadeIn} 0.5s ease-out forwards;
    animation-delay: ${props => props.delay + 0.6}s;

    &:hover {
        background-color: ${props => props.theme.colors.secondary};
        transform: scale(1.05);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 768px) {
        font-size: 0.8rem;
        padding: 8px 16px;
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    opacity: ${props => (props.isOpen ? 1 : 0)};
    visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
    transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const ModalContent = styled.div`
    position: relative;
    background-color: ${props => props.theme.colors.background};
    padding: 24px;
    margin: 20px;
    border-radius: 12px;
    max-width: 500px;
    width: 90%;
    text-align: center;
    animation: ${modalFadeIn} 0.3s ease-out forwards;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    max-height: 90vh;
    overflow-y: auto;
`;

const ModalLogo = styled.img`
    max-width: 140px;
    margin-bottom: 15px;
`;

const ModalTitle = styled.h2`
    color: ${props => props.theme.colors.primary};
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 10px;

    @media (max-width: 768px) {
        font-size: 1.2rem;
    }
`;

const ModalText = styled.p`
    color: ${props => props.theme.colors.text};
    font-size: 1rem;
    margin: 0 0 15px;
    line-height: 1.5;

    @media (max-width: 768px) {
        font-size: 0.9rem;
    }
`;

const ModalBikeThumbnail = styled.img`
    max-height: 90px;
    max-width: 140px;
    object-fit: contain;
    margin: 5px auto 15px;
    border-radius: 6px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    display: block;
`;

const ModalButtonsContainer = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;
`;

const ModalButton = styled.a`
    display: inline-flex;
    align-items: center;
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    padding: 10px 20px;
    border-radius: 5px;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    animation: ${fadeIn} 0.5s ease-out forwards;
    animation-delay: ${props => props.delay || 0}s;
    transition: all 0.3s ease;

    &:hover {
        background-color: ${props => props.theme.colors.secondary};
        transform: scale(1.05);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 768px) {
        font-size: 0.8rem;
        padding: 8px 16px;
    }
`;

const QRCodeSection = styled.div`
    margin: 20px auto 5px;
    padding: 12px;
    background-color: ${props => props.theme.colors.backgroundItemCard};
    border: 1px solid ${props => props.theme.colors.secondary}25;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 220px;
`;

const QRCodeImage = styled.img`
    width: 130px;
    height: 130px;
    border-radius: 6px;
    margin-bottom: 8px;
`;

const QRCodeLabel = styled.span`
    font-size: 0.8rem;
    color: ${props => props.theme.colors.text || '#000000'};
    line-height: 1.3;
    text-align: center;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    color: ${props => props.theme.colors.text || '#333333'};
    font-size: 1.4rem;
    font-weight: 700;
    position: absolute;
    top: 10px;
    right: 15px;
    cursor: pointer;
    transition: color 0.3s ease;

    &:hover {
        color: ${props => props.theme.colors.primary};
    }
`;

const BikeCard = ({rentUser, item, index, coverUrl}) => {
    const {t} = useTranslation();
    const delay = index * 0.1;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDailyPricesOpen, setIsDailyPricesOpen] = useState(false);
    const [isMultiDayPricesOpen, setIsMultiDayPricesOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth > 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const sizeOrder = ['KIDS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];

    const sortSizes = (sizes) => {
        if (!sizes) return [];
        return [...sizes].sort((a, b) => {
            const indexA = sizeOrder.indexOf(a);
            const indexB = sizeOrder.indexOf(b);
            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    };

    const getPriceInfo = () => {
        if (!item.singleDayPrices || !rentUser?.rentSlotConfiguration) return [];
        return item.singleDayPrices.map((price) => {
            const slot = rentUser.rentSlotConfiguration.find((config) => config.type === price.type);
            return {
                type: price.type,
                price: price.price,
                time: slot ? `${slot.from}-${slot.to}` : 'N/D',
                description: slot ? slot.description : 'N/D',
            };
        });
    };

    const getMultiDayPrices = () => {
        if (!item.prices) return [];
        const sortedPrices = [...item.prices].sort((a, b) => parseInt(a.day) - parseInt(b.day));
        let cumulativeCost = 0;
        return sortedPrices.map((price) => {
            cumulativeCost += price.cost;
            return {
                day: parseInt(price.day),
                totalCost: cumulativeCost,
            };
        });
    };

    const sortedSizes = sortSizes(item.sizes);
    const priceInfo = getPriceInfo();
    const multiDayPrices = getMultiDayPrices();

    const handleCardClick = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            setIsModalOpen(false);
        }
    };

    const toggleDailyPrices = () => {
        setIsDailyPricesOpen(prev => !prev);
    };

    const toggleMultiDayPrices = () => {
        setIsMultiDayPricesOpen(prev => !prev);
    };

    return (
        <>
            <Card delay={delay} onClick={handleCardClick} aria-label={t('bikeCard', {name: item.name})}>
                <BikeInnerContainer>
                    <div style={{flex: 1}}>
                        <BikeName>{item.name}</BikeName>
                        {coverUrl ? (
                            <BikeImage src={coverUrl} alt={item.name} delay={delay}/>
                        ) : (
                            <Placeholder/>
                        )}
                    </div>
                    <InfoContainer>
                        <BikeInfo>
                            <strong>{t('category')}:</strong> {item.category || 'N/D'}
                        </BikeInfo>
                        <BikeInfo>
                            <strong>{t('sizes')}:</strong> {sortedSizes.join(', ') || 'N/D'}
                        </BikeInfo>
                    </InfoContainer>
                    <div>
                        {!isDesktop && (
                            <PriceSectionHeader
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDailyPrices();
                                }}
                                role="button"
                                aria-expanded={isDailyPricesOpen}
                                aria-controls="daily-prices"
                                aria-label={t('dailyPricesToggle')}
                            >
                                {t('dailyPrices')}
                                {isDailyPricesOpen ? <FaChevronUp/> : <FaChevronDown/>}
                            </PriceSectionHeader>
                        )}
                        <InfoContainerPrices
                            id="daily-prices"
                            isOpen={isDailyPricesOpen}
                            isDesktop={isDesktop}
                            aria-label={t('dailyPrices')}
                        >
                            {priceInfo.map((info, idx) => (
                                <BikePrice key={idx} delay={delay}>
                                    <strong>{t(info.type)}:</strong> €{info.price} ({info.time})
                                </BikePrice>
                            ))}
                        </InfoContainerPrices>
                    </div>
                    <div>
                        {!isDesktop && (
                            <PriceSectionHeader
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMultiDayPrices();
                                }}
                                role="button"
                                aria-expanded={isMultiDayPricesOpen}
                                aria-controls="multi-day-prices"
                                aria-label={t('multiDayPricesToggle')}
                            >
                                {t('multiDayPrices')}
                                {isMultiDayPricesOpen ? <FaChevronUp/> : <FaChevronDown/>}
                            </PriceSectionHeader>
                        )}
                        <MultiDayPricesContainer
                            id="multi-day-prices"
                            isOpen={isMultiDayPricesOpen}
                            isDesktop={isDesktop}
                            aria-label={t('multiDayPrices')}
                        >
                            {multiDayPrices.map((price, idx) => (
                                <BikePrice key={idx} delay={delay}>
                                    <strong>{idx === 0 ? t('day') : t('bikeCardDays', {count: price.day})}:</strong> €{price.totalCost}
                                </BikePrice>
                            ))}
                        </MultiDayPricesContainer>
                    </div>
                </BikeInnerContainer>
                <BookButton onClick={handleCardClick} aria-label={t('book')}>
                    {t('book')}
                </BookButton>
            </Card>

            <ModalOverlay isOpen={isModalOpen} onClick={handleOverlayClick}>
                <ModalContent>
                    <CloseButton onClick={handleCloseModal} aria-label={t('closeModal')}>×</CloseButton>
                    <a href="https://locemsolutions.com" target="_blank" rel="noopener noreferrer">
                        <ModalLogo
                            src="https://firebasestorage.googleapis.com/v0/b/locem-prod.appspot.com/o/com%2Flocemsolutions%2Flocem%2Flogoapp%2Flogo.png?alt=media&token=77dc1a42-bc70-4d38-aebc-d9cd3c9990ed"
                            alt="LoceM Logo"
                        />
                    </a>
                    <ModalTitle>{t('downloadAppTitle')}</ModalTitle>
                    <ModalText>
                        {t('bookModalPrompt', {name: item.name, defaultValue: t('downloadAppText')})}
                    </ModalText>
                    {coverUrl && (
                        <ModalBikeThumbnail src={coverUrl} alt={item.name} />
                    )}
                    <ModalButtonsContainer>
                        <ModalButton
                            delay={0.1}
                            href="https://play.google.com/store/apps/details?id=com.locemsolutions.locem"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FaGooglePlay style={{marginRight: '10px'}}/> {t('googlePlay')}
                        </ModalButton>
                        <ModalButton
                            delay={0.2}
                            href="https://apps.apple.com/us/app/locem/id6449721104"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FaApple style={{marginRight: '10px'}}/> {t('appStore')}
                        </ModalButton>
                    </ModalButtonsContainer>
                    {isDesktop && (
                        <QRCodeSection>
                            <QRCodeImage
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=2&data=${encodeURIComponent('https://locemsolutions.com')}`}
                                alt="LoceM QR Code"
                                loading="lazy"
                            />
                            <QRCodeLabel>{t('scanToDownload')}</QRCodeLabel>
                        </QRCodeSection>
                    )}
                </ModalContent>
            </ModalOverlay>
        </>
    );
};

export default BikeCard;
